// Epoch 25: Centralized Trial State Management
// This provider is the single source of truth for all trial-related state
// Replaces the distributed logic from Epoch 23 v2

import React, { createContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { supabase } from '../supabase/client';
import { useAuthSession } from './AuthSessionContext';

// Trial status from Edge Function
interface TrialStatusData {
  is_trial_user: boolean;
  trial_active: boolean;
  trial_expired: boolean;
  days_remaining: number;
  trial_start_date: string | null;
  trial_end_date: string | null;
  can_access_campaigns: boolean;
  can_create_campaigns: boolean;
  campaigns_created: number;
  max_campaigns: number;
  leads_exported: number;
  plan_type?: string;
  billing_status?: string;
  paid_through_date?: string | null;
  is_paid_through_lapsed?: boolean;
}

// Unified trial state
type TrialState = 
  | { type: 'loading' }
  | { type: 'trial_active'; data: TrialStatusData }
  | { type: 'trial_expired'; data: TrialStatusData }
  | { type: 'paid_user'; planType: string }
  | { type: 'paid_lapsed'; data: TrialStatusData }
  | { type: 'free_user' }
  | { type: 'csr_user' }
  | { type: 'error'; error: string };

interface TrialContextValue {
  state: TrialState;
  
  // Computed values for easy access
  isLoading: boolean;
  isTrialUser: boolean;
  isTrialActive: boolean;
  isTrialExpired: boolean;
  isPaidUser: boolean;
  isPaidLapsed: boolean;
  isFreeUser: boolean;
  
  // Trial data
  daysRemaining: number;
  campaignsUsed: number;
  campaignsRemaining: number;
  maxCampaigns: number;
  trialStartDate: string | null;
  trialEndDate: string | null;
  
  // Actions
  refreshTrialStatus: () => Promise<void>;
  startTrial: (email: string, userData?: Record<string, unknown>) => Promise<{ success: boolean; error?: string }>;
}

const TrialContext = createContext<TrialContextValue | undefined>(undefined);

// Export the context for use in hooks file
export { TrialContext };

interface TrialProviderProps {
  children: ReactNode;
}

export const TrialProvider: React.FC<TrialProviderProps> = ({ children }) => {
  const { session, userRole } = useAuthSession();
  const [state, setState] = useState<TrialState>({ type: 'loading' });
  const [hasInitialized, setHasInitialized] = useState(false);
  // Keep a ref so fetchTrialStatus can read current role without it being a dep
  const userRoleRef = useRef(userRole);
  userRoleRef.current = userRole;

  console.log('🔄 TrialProvider render - state:', state.type, 'initialized:', hasInitialized);

  // Fetch trial status from Edge Function
  const fetchTrialStatus = useCallback(async () => {
    try {
      // Special handling for CSR users - they never see trial content
      if (userRoleRef.current === 'csr') {
        console.log('🔐 TrialProvider: CSR user detected, skipping trial check');
        setState({ type: 'csr_user' });
        return;
      }

      if (!session?.user?.id) {
        console.log('🔐 TrialProvider: No authenticated user');
        setState({ type: 'free_user' });
        return;
      }

      console.log('🔍 TrialProvider: Fetching trial status for user:', session.user.id);

      // Use the canonical initializeTrial Edge Function
      const { data: response, error } = await supabase.functions.invoke('initializeTrial', {
        body: { action: 'get_trial_status' }
      });

      if (error) {
        console.error('❌ TrialProvider: Failed to fetch trial status:', error);
        setState({ type: 'error', error: error.message });
        return;
      }

      // Handle nested data structure from Edge Function
      const trialData = (response?.data || response) as TrialStatusData;
      
      console.log('✅ TrialProvider: Trial data received:', {
        is_trial_user: trialData.is_trial_user,
        trial_active: trialData.trial_active,
        days_remaining: trialData.days_remaining,
        billing_status: trialData.billing_status
      });

      // Determine the state based on trial data
      if (!trialData.is_trial_user) {
        // Epoch 85: an offline-paid account whose prepaid period lapsed still
        // has billing_status 'active' -- check the lapsed flag first so it
        // doesn't fall through into paid_user. Stripe-billed customers never
        // have this flag set, so they're unaffected.
        if (trialData.is_paid_through_lapsed) {
          setState({ type: 'paid_lapsed', data: trialData });
        } else if (trialData.billing_status === 'active' || trialData.plan_type) {
          setState({ type: 'paid_user', planType: trialData.plan_type || 'standard' });
        } else {
          setState({ type: 'free_user' });
        }
      } else if (trialData.trial_active) {
        setState({ type: 'trial_active', data: trialData });
      } else if (trialData.trial_expired) {
        setState({ type: 'trial_expired', data: trialData });
      } else {
        // Trial user but not active or expired - edge case
        setState({ type: 'free_user' });
      }
      
      // Mark as initialized after first successful fetch
      setHasInitialized(true);

    } catch (err) {
      console.error('🚨 TrialProvider: Unexpected error:', err);
      setState({ type: 'error', error: 'Failed to load trial status' });
    }
  // Only session user ID matters — primitive avoids re-creation on token refresh or role update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  // Start a new trial
  const startTrial = useCallback(async (email: string, userData?: Record<string, unknown>) => {
    try {
      if (!session?.user) {
        return { success: false, error: 'No authenticated user' };
      }

      console.log('🚀 TrialProvider: Starting trial for:', email);

      // Use initializeTrial with initialize_trial action
      const { error } = await supabase.functions.invoke('initializeTrial', {
        body: { 
          action: 'initialize_trial',
          email,
          user_id: session.user.id,
          ...userData
        }
      });

      if (error) {
        console.error('❌ TrialProvider: Failed to start trial:', error);
        return { success: false, error: error.message };
      }

      // Refresh trial status after starting
      await fetchTrialStatus();
      
      return { success: true };
    } catch (err) {
      console.error('🚨 TrialProvider: Error starting trial:', err);
      return { success: false, error: 'Failed to start trial' };
    }
  }, [session?.user, fetchTrialStatus]);

  // Refresh trial status - don't reset to loading if we already have data
  const refreshTrialStatus = useCallback(async () => {
    console.log('🔄 TrialProvider: Refreshing trial status');
    // Only set loading if we don't have data yet
    if (!hasInitialized) {
      setState({ type: 'loading' });
    }
    await fetchTrialStatus();
  }, [fetchTrialStatus, hasInitialized]);

  // Initial load - only fetch once on mount or when session changes
  useEffect(() => {
    // Only fetch if we have a session and haven't initialized yet
    if (session?.user?.id && !hasInitialized) {
      console.log('🚀 TrialProvider: Initializing trial status fetch');
      fetchTrialStatus();
    }
  }, [session?.user?.id, hasInitialized, fetchTrialStatus]);

  // Computed values for easy access
  const isLoading = state.type === 'loading';
  const isTrialUser = state.type === 'trial_active' || state.type === 'trial_expired';
  const isTrialActive = state.type === 'trial_active';
  const isTrialExpired = state.type === 'trial_expired';
  const isPaidUser = state.type === 'paid_user';
  const isPaidLapsed = state.type === 'paid_lapsed';
  const isFreeUser = state.type === 'free_user';

  // Extract trial data based on state
  let daysRemaining = 0;
  let campaignsUsed = 0;
  let campaignsRemaining = 0;
  let maxCampaigns = 0;
  let trialStartDate: string | null = null;
  let trialEndDate: string | null = null;

  if (state.type === 'trial_active' || state.type === 'trial_expired') {
    daysRemaining = state.data.days_remaining;
    campaignsUsed = state.data.campaigns_created;
    maxCampaigns = state.data.max_campaigns;
    campaignsRemaining = Math.max(0, maxCampaigns - campaignsUsed);
    trialStartDate = state.data.trial_start_date;
    trialEndDate = state.data.trial_end_date;
  }

  const contextValue: TrialContextValue = {
    state,
    isLoading,
    isTrialUser,
    isTrialActive,
    isTrialExpired,
    isPaidUser,
    isPaidLapsed,
    isFreeUser,
    daysRemaining,
    campaignsUsed,
    campaignsRemaining,
    maxCampaigns,
    trialStartDate,
    trialEndDate,
    refreshTrialStatus,
    startTrial
  };

  return (
    <TrialContext.Provider value={contextValue}>
      {children}
    </TrialContext.Provider>
  );
};

// Hooks are exported from a separate file for Fast Refresh compatibility
// See: src/hooks/useTrialContext.ts